import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import "core-js/modules/web.dom-collections.iterator.js";
import dateFormat from "dateformat";
import { observer } from "mobx-react";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import JulianDate from "terriajs-cesium/Source/Core/JulianDate";
import CommonStrata from "../../../Models/Definition/CommonStrata";
import Box from "../../../Styled/Box";
import Button from "../../../Styled/Button";
import Icon, { StyledIcon } from "../../../Styled/Icon";
import Spacing from "../../../Styled/Spacing";
import Text, { TextSpan } from "../../../Styled/Text";
import { formatDateTime } from "../../BottomDock/Timeline/DateFormats";
import DateTimePicker from "../../BottomDock/Timeline/DateTimePicker";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DatePicker = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    heading,
    item,
    onDateSet
  } = props;
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentDate = useMemo(() => {
    const date = item.currentDiscreteJulianDate;
    return date && JulianDate.toDate(date);
  }, [item.currentDiscreteJulianDate]);
  const formattedCurrentDate = useMemo(() => {
    if (currentDate === undefined) {
      return;
    }
    const dateFormatting = undefined; // TODO
    const formattedDate = dateFormatting !== undefined ? dateFormat(currentDate, dateFormatting) : formatDateTime(currentDate);
    return formattedDate;
  }, [currentDate]);
  const toggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const changeCurrentDate = useCallback(date => {
    item.setTrait(CommonStrata.user, "currentTime", date.toISOString());
    onDateSet();
  }, [item, onDateSet]);
  const moveToPreviousDate = useCallback(() => {
    item.moveToPreviousDiscreteTime(CommonStrata.user);
    onDateSet();
  }, [item, onDateSet]);
  const moveToNextDate = useCallback(() => {
    item.moveToNextDiscreteTime(CommonStrata.user);
    onDateSet();
  }, [item, onDateSet]);
  useImperativeHandle(ref, () => {
    return {
      open: () => {
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
      }
    };
  }, []);
  return /*#__PURE__*/_jsxs(Box, {
    column: true,
    centered: true,
    flex: 1,
    children: [/*#__PURE__*/_jsx(Spacing, {
      bottom: 4
    }), /*#__PURE__*/_jsxs(Box, {
      centered: true,
      children: [/*#__PURE__*/_jsx(_StyledStyledIcon, {
        light: true,
        styledWidth: "21px",
        glyph: Icon.GLYPHS.calendar2,
        $_css: "margin-top:-2px;"
      }), /*#__PURE__*/_jsx(Spacing, {
        right: 2
      }), /*#__PURE__*/_jsx(Text, {
        textLight: true,
        extraLarge: true,
        children: heading
      })]
    }), /*#__PURE__*/_jsx(Spacing, {
      bottom: 2
    }), /*#__PURE__*/_jsxs(Box, {
      children: [/*#__PURE__*/_jsx(PrevButton, {
        disabled: item.isPreviousDiscreteTimeAvailable === false,
        title: t("diffTool.datePicker.previousDateTitle"),
        onClick: moveToPreviousDate
      }), /*#__PURE__*/_jsx(DateButton, {
        primary: true,
        isOpen: isOpen,
        onClick: toggleOpen,
        title: t("diffTool.datePicker.dateButtonTitle"),
        children: /*#__PURE__*/_jsx(TextSpan, {
          extraLarge: true,
          children: formattedCurrentDate || "-"
        })
      }), /*#__PURE__*/_jsx(NextButton, {
        disabled: item.isNextDiscreteTimeAvailable === false,
        title: t("diffTool.datePicker.nextDateTitle"),
        onClick: moveToNextDate
      })]
    }), /*#__PURE__*/_jsx("div", {
      style: {
        display: isOpen ? "block" : "none",
        position: "absolute"
      },
      children: /*#__PURE__*/_jsx(DateTimePicker, {
        currentDate: currentDate,
        dates: item.objectifiedDates,
        onChange: changeCurrentDate,
        openDirection: "up",
        isOpen: isOpen,
        onClose: () => setIsOpen(false)
      })
    }), /*#__PURE__*/_jsx(Spacing, {
      bottom: 4
    })]
  });
});
DatePicker.displayName = "DatePicker";
const PagerButton = styled(Button).attrs({
  iconProps: {
    css: "margin-right:0;"
  }
}).withConfig({
  displayName: "DatePicker__PagerButton",
  componentId: "sc-11kes1x-0"
})(["cursor:pointer;background-color:", ";width:40px;border:1px solid transparent;", " flex-direction:column;"], props => props.theme.colorPrimary, _ref => {
  let {
    theme
  } = _ref;
  return theme.centerWithFlex();
});
const PrevButton = styled(PagerButton).attrs({
  renderIcon: () => /*#__PURE__*/_jsx(_StyledStyledIcon2, {
    light: true,
    styledWidth: "15px",
    glyph: Icon.GLYPHS.arrowDown
  })
}).withConfig({
  displayName: "DatePicker__PrevButton",
  componentId: "sc-11kes1x-1"
})(["", " margin-right:1px;"], _ref2 => {
  let {
    theme
  } = _ref2;
  return theme.borderRadiusLeft(theme.radius40Button);
});
const NextButton = styled(PagerButton).attrs({
  renderIcon: () => /*#__PURE__*/_jsx(_StyledStyledIcon3, {
    light: true,
    styledWidth: "15px",
    glyph: Icon.GLYPHS.arrowDown
  })
}).withConfig({
  displayName: "DatePicker__NextButton",
  componentId: "sc-11kes1x-2"
})(["", " margin-left:1px;"], _ref3 => {
  let {
    theme
  } = _ref3;
  return theme.borderRadiusRight(theme.radius40Button);
});
const DateButton = styled(Button).withConfig({
  displayName: "DatePicker__DateButton",
  componentId: "sc-11kes1x-3"
})(["z-index:0;", ";border-radius:0px;border:1px solid ", ";min-width:235px;@media (max-width:", "px){min-width:150px;}"], props => props.isOpen && "z-index: 1000;", props => props.theme.colorPrimary, props => props.theme.lg);
export default observer(DatePicker);
var _StyledStyledIcon = styled(StyledIcon).withConfig({
  displayName: "DatePicker___StyledStyledIcon",
  componentId: "sc-11kes1x-4"
})(["", ""], p => p.$_css);
var _StyledStyledIcon2 = styled(StyledIcon).withConfig({
  displayName: "DatePicker___StyledStyledIcon2",
  componentId: "sc-11kes1x-5"
})(["transform:rotate(90deg);"]);
var _StyledStyledIcon3 = styled(StyledIcon).withConfig({
  displayName: "DatePicker___StyledStyledIcon3",
  componentId: "sc-11kes1x-6"
})(["transform:rotate(270deg);"]);